import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Skeleton } from '@solid/ui';
import { Clock, AlertCircle, RefreshCw } from 'lucide-react';
import { financialsTimeApi, type ClientHours, type HourBank } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/utils';
import { useAuthStore, getIsAgencyAdmin, getIsAgencyMember } from '@/stores/auth.store';

/** One place decides how an hour figure is written, so no two lines disagree. */
function hours(value: number): string {
  // Whole hours read as whole hours; a fraction keeps one decimal. Two decimals
  // on a support balance is false precision — nobody bills to 0.01 h.
  return Number.isInteger(value) ? `${value}h` : `${value.toFixed(1)}h`;
}

/**
 * The bar's colour follows the bank's OWN status, not a threshold invented
 * here. Financials decides what "low" means — a second definition on this side
 * is how two screens start disagreeing about the same client.
 */
const BAR: Record<string, string> = {
  good: 'bg-success',
  ok: 'bg-accent',
  low: 'bg-amber-500',
  exhausted: 'bg-error',
  overage: 'bg-error',
};

function runsOutSentence(bank: HourBank): string | null {
  if (!bank.runsOutOn) return null;
  const when = new Date(bank.runsOutOn).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  // The reason is not a detail: hours that EXPIRE are gone whether or not
  // anybody burns them, so "at this rate" would be false for that case.
  return bank.runsOutReason === 'expiry'
    ? `Hours expire ${when}`
    : `At ${hours(bank.burnPerWeek)}/week, runs out ${when}`;
}

/**
 * Support hours left on a project, read from financials.
 *
 * ⚠ THE BANK BELONGS TO THE CLIENT. Several support projects can draw the same
 * pool down, which is why the client is named in the heading and the sharing is
 * spelled out underneath. A bare "29h left" here would read as this project's
 * allowance, and that is a different number — financials is explicit about this
 * in its README and this card is the reason why.
 *
 * Renders NOTHING at all when there is no bank: most projects are not support
 * agreements, and "0h left" for a project that never had an allowance is worse
 * than silence.
 *
 * CLIENT members get their OWN variant (Jesse, 2026-08-23: "they should see
 * it in the hub, just not the burn pace"): balance, what they bought and
 * used, contractual expiry, and a way to buy more — never the burn pace or
 * the runs-out projection. The server enforces the split; this component just
 * renders what each caller is allowed to receive.
 */
export function HoursRemainingCard({
  projectId,
  clientOrgId,
}: {
  projectId: string;
  /** The org whose admins hold purchase rights — projectOrg(project)?.id. */
  clientOrgId?: string;
}) {
  const { user } = useAuthStore();
  const isAgencyMember = getIsAgencyMember(user);

  if (!isAgencyMember) return <ClientHoursCard projectId={projectId} clientOrgId={clientOrgId} />;
  return <AgencyHoursCard projectId={projectId} canRefresh={getIsAgencyAdmin(user)} />;
}

function AgencyHoursCard({ projectId, canRefresh }: { projectId: string; canRefresh: boolean }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['financials-hours', projectId],
    queryFn: () => financialsTimeApi.hours(projectId),
    staleTime: 5 * 60_000,
    // A 502/503 here means financials is down or unconfigured. That is not
    // something this card should nag about — it just does not appear.
    retry: false,
  });

  // The refresh answers with the same shape as the read, so the card takes
  // the fresh figures straight from it rather than reading a second time.
  const refresh = useMutation({
    mutationFn: () => financialsTimeApi.refreshHours(projectId),
    onSuccess: (hours) => queryClient.setQueryData(['financials-hours', projectId], hours),
  });

  if (isLoading) {
    return <Skeleton className="h-32 rounded-xl" />;
  }

  // No bank, or financials unreachable — absent, never a zero.
  if (isError || !data?.bank) return null;

  const bank = data.bank;
  const pct = bank.pctRemaining === null ? null : Math.round(bank.pctRemaining * 100);
  const isOver = bank.overage > 0;

  return (
    <Card data-testid="hours-remaining">
      <div className="mb-3 flex items-center gap-2">
        <Clock className="h-5 w-5 text-accent" />
        <h2 className="text-lg font-semibold text-secondary">Support hours</h2>
        {canRefresh && (
          <button
            type="button"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            title="Refresh support hours"
            aria-label={refresh.isPending ? 'Refreshing support hours' : 'Refresh support hours'}
            className="ml-auto rounded-md p-1.5 text-grey-500 transition-colors hover:bg-grey-100 hover:text-secondary disabled:opacity-60"
          >
            <RefreshCw className={cn('h-4 w-4', refresh.isPending && 'animate-spin')} />
          </button>
        )}
      </div>

      <div className="flex items-baseline gap-2">
        <span className="text-heading-1 tabular-nums text-secondary">{hours(bank.balance)}</span>
        <span className="text-sm text-grey-500">
          left of {hours(bank.purchased)}
          {pct !== null && ` · ${pct}%`}
        </span>
      </div>

      {pct !== null && (
        <div
          className="mt-3 h-2 overflow-hidden rounded-full bg-grey-100"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${hours(bank.balance)} of ${hours(bank.purchased)} remaining`}
        >
          <div
            className={cn('h-full rounded-full', BAR[bank.status ?? 'ok'] ?? 'bg-accent')}
            style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
          />
        </div>
      )}

      {/* Overage is reported separately BECAUSE the balance never goes negative
          — a bank reading "−17h" invites somebody to treat it as hours we are
          owed. Hidden, it would look like the work was free. */}
      {isOver && (
        <p className="mt-3 flex items-center gap-1.5 text-sm font-medium text-error">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {hours(bank.overage)} worked beyond the bank
        </p>
      )}

      {runsOutSentence(bank) && (
        <p className="mt-2 text-sm text-grey-500">{runsOutSentence(bank)}</p>
      )}

      {/* The whole reason this line exists: naming whose pool it is, and who
          else spends from it. Without it the figure above means the wrong thing. */}
      <p className="mt-3 border-t border-grey-100 pt-3 text-xs text-grey-500">
        {bank.clientName}&rsquo;s support bank
        {bank.projectCount > 1 && ` · shared by ${bank.projectCount} projects`}
      </p>

      {refresh.isError && (
        <p className="mt-2 text-xs text-error" role="alert">
          Couldn&rsquo;t refresh from financials — try again in a minute.
        </p>
      )}
    </Card>
  );
}


/**
 * The client's own view of the same bank. Balance-not-pace, and the door to
 * buying more: financials emails their billing contact the secure hours page
 * (card payment or invoice request live there) — the link itself never
 * touches the hub.
 */
function ClientHoursCard({ projectId, clientOrgId }: { projectId: string; clientOrgId?: string }) {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const { user } = useAuthStore();
  // Purchasing is an ADMIN act: the server enforces it (403), this just keeps
  // the button honest for Members instead of offering an action that refuses.
  const membership = (user?.organizations ?? []).find((o: { id?: string }) => o.id === clientOrgId);
  const canRequest =
    !!membership &&
    ((membership as { role?: string }).role === 'org_admin' ||
      (membership as { role?: string }).role === 'org_owner');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['financials-client-hours', projectId],
    queryFn: () => financialsTimeApi.clientHours(projectId),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const request = useMutation({
    mutationFn: () => financialsTimeApi.requestHoursLink(projectId),
    onSuccess: (result) => setSentTo(result.to || 'your billing contact'),
  });

  if (isLoading) return <Skeleton className="h-32 rounded-xl" />;
  if (isError || !data?.bank) return null;

  const bank: ClientHours = data.bank as ClientHours;
  const pct = bank.pctRemaining === null ? null : Math.round(bank.pctRemaining * 100);
  const expiring = bank.expiringSoon[0];

  return (
    <Card data-testid="client-hours-remaining">
      <div className="mb-3 flex items-center gap-2">
        <Clock className="h-5 w-5 text-accent" />
        <h2 className="text-lg font-semibold text-secondary">Support hours</h2>
      </div>

      <div className="flex items-baseline gap-2">
        <span className="text-heading-1 tabular-nums text-secondary">{hours(bank.balance)}</span>
        <span className="text-sm text-grey-500">
          left of {hours(bank.purchased)}
          {pct !== null && ` · ${pct}%`}
        </span>
      </div>

      {pct !== null && (
        <div
          className="mt-3 h-2 overflow-hidden rounded-full bg-grey-100"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${hours(bank.balance)} of ${hours(bank.purchased)} remaining`}
        >
          <div
            className={cn('h-full rounded-full', BAR[bank.status ?? 'ok'] ?? 'bg-accent')}
            style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
          />
        </div>
      )}

      {bank.overage > 0 && (
        <p className="mt-3 flex items-center gap-1.5 text-sm font-medium text-error">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {hours(bank.overage)} worked beyond the bank
        </p>
      )}

      {/* Contractual expiry is the client's own fact — the honest nudge to use
          or renew. The burn pace is not, and never renders here. */}
      {expiring && (
        <p className="mt-2 text-sm text-grey-500">
          {hours(expiring.remaining)} expire{' '}
          {new Date(expiring.expiresAt).toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </p>
      )}

      {bank.awaitingPayment > 0 && (
        <p className="mt-2 text-sm text-grey-500">
          {hours(bank.awaitingPayment)} purchased, awaiting payment
        </p>
      )}

      <div className="mt-3 border-t border-grey-100 pt-3">
        {sentTo ? (
          <p className="text-sm text-grey-600" role="status">
            Sent — check {sentTo} for a secure link to buy more hours or request an invoice.
          </p>
        ) : !canRequest ? (
          <p className="text-xs text-grey-500">
            Need more hours? Ask one of your organization&rsquo;s admins — buying is an admin
            action.
          </p>
        ) : (
          <>
            <button
              type="button"
              disabled={request.isPending}
              onClick={() => request.mutate()}
              className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              {request.isPending ? 'Sending…' : 'Get more hours'}
            </button>
            <p className="mt-1.5 text-xs text-grey-500">
              We&rsquo;ll email your billing contact a secure link to buy more hours by card, or to
              request an invoice.
            </p>
            {request.isError && (
              <p className="mt-1 text-xs text-error" role="alert">
                Could not send the link — try again, or contact us directly.
              </p>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
